import { PIPE_TYPES, FIT_STATES } from './model';
import { LOG_COLORS } from '../hooks/useLoggers';
import { spoken } from './speech/spoken';
import { playStored } from './speech/stored';
import { RULES } from './speech/rules';

// accessibility: red-green safe colours (Okabe-Ito) and spoken help / stake-out / voice commands
const KEY = 'giss_a11y';
export const getA11y = () => { try { return { cb: false, voice: false, gender: 'f', voiceName: '', ...JSON.parse(localStorage.getItem(KEY) || '{}') }; } catch { return { cb: false, voice: false, gender: 'f', voiceName: '' }; } };
export function setA11y(p) {
  const prev = getA11y(), v = { ...prev, ...p };
  localStorage.setItem(KEY, JSON.stringify(v));
  window.dispatchEvent(new Event('giss-a11y'));
  if (prev.cb !== v.cb) { if (!localStorage.getItem('giss_profiles')) sessionStorage.setItem('giss_fresh_keep', '1'); window.location.reload(); }
}

if (getA11y().cb) {
  Object.assign(PIPE_TYPES, { water: '#0072B2', sewer: '#7A4A00', gas: '#F0E442', fire: '#CC79A7', power: '#D55E00', telecom: '#56B4E9', other: '#E69F00', leak: '#882255' });
  Object.assign(FIT_STATES, { open: '#0072B2', partial: '#E69F00', ok: '#56B4E9', defect: '#D55E00', check: '#F0E442' });
  Object.assign(LOG_COLORS, { perm: '#56B4E9', green: '#009E73', yellow: '#F0E442', red: '#D55E00' });
  document.documentElement.classList.add('cb');
}

const LOC = { de: 'de-DE', en: 'en-GB', it: 'it-IT', es: 'es-ES', pt: 'pt-PT', fr: 'fr-FR', sq: 'sq-AL' };

// any stored colour (catalogue items etc.) -> nearest red-green safe colour by hue
const CB_ON = getA11y().cb;
export function cbColor(c) {
  if (!CB_ON || typeof c !== 'string' || !/^#[0-9a-f]{6}$/i.test(c)) return c;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16) / 255);
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (d < 0.15) return c;
  let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  if (mx < 0.6 && h >= 15 && h < 45) return '#7A4A00';
  return h < 15 || h >= 345 ? '#D55E00' : h < 45 ? '#E69F00' : h < 70 ? '#F0E442' : h < 170 ? '#009E73' : h < 200 ? '#56B4E9' : h < 250 ? '#0072B2' : h < 300 ? '#CC79A7' : '#882255';
}

// spoken abbreviations -> full words
const ABBR = {
  de: { 'z. B.': 'zum Beispiel', 'z.B.': 'zum Beispiel', 'd. h.': 'das heißt', 'd.h.': 'das heißt', 'u. a.': 'unter anderem', 'u. v. m.': 'und vieles mehr', 'u.v.m.': 'und vieles mehr', 'u.a.': 'unter anderem', 'bzw.': 'beziehungsweise', 'ca.': 'circa', 'usw.': 'und so weiter', 'evtl.': 'eventuell', 'ggf.': 'gegebenenfalls', 'inkl.': 'inklusive', 'max.': 'maximal', 'min.': 'mindestens', 'Nr.': 'Nummer', 'Std.': 'Stunden', 'bzgl.': 'bezüglich', 'm³': 'Kubikmeter', 'mm²': 'Quadratmillimeter', 'l/min': 'Liter pro Minute', 'l/s': 'Liter pro Sekunde', 'km/h': 'Kilometer pro Stunde', '°': ' Grad', 'cm': 'Zentimeter', 'mm': 'Millimeter', 'km': 'Kilometer', 'ppm': 'p p m', ' m ': ' Meter ', 'DN': 'D N', 'PDF': 'P D F', 'GPS': 'G P S', 'RTK': 'R T K', '→': ', dann ', '&': ' und ' },
  en: { 'e.g.': 'for example', 'i.e.': 'that is', 'etc.': 'et cetera', 'approx.': 'approximately', 'no.': 'number', 'm³': 'cubic metres', 'l/min': 'litres per minute', 'l/s': 'litres per second', '°': ' degrees', 'cm': 'centimetres', '→': ', then ', '&': ' and ' },
  it: { 'ad es.': 'ad esempio', 'ecc.': 'eccetera', 'm³': 'metri cubi', '°': ' gradi', 'cm': 'centimetri', '→': ', poi ' },
  es: { 'p. ej.': 'por ejemplo', 'etc.': 'etcétera', 'aprox.': 'aproximadamente', 'm³': 'metros cúbicos', '°': ' grados', 'cm': 'centímetros', '→': ', luego ' },
  pt: { 'p. ex.': 'por exemplo', 'etc.': 'et cetera', 'm³': 'metros cúbicos', '°': ' graus', 'cm': 'centímetros', '→': ', depois ' },
  fr: { 'p. ex.': 'par exemple', 'etc.': 'et cetera', 'env.': 'environ', 'm³': 'mètres cubes', '°': ' degrés', 'cm': 'centimètres', '→': ', puis ' },
};
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export function expandAbbr(text, l = 'de') {
  if (RULES[l]) return spoken(text, l);
  let s = ` ${text} `;
  Object.entries(ABBR[l] || {}).sort((a, b) => b[0].length - a[0].length).forEach(([k, v]) => {
    const word = /^[\p{L}]+$/u.test(k);
    s = s.replace(new RegExp(word ? `(?<=[\\s\\d(])${esc(k)}(?=[\\s.,;:)!?])` : esc(k), 'gu'), v);
  });
  return speakSymbols(s, l).replace(/[„“”"«»]/g, '').replace(/\s+/g, ' ').trim();
}

// icons and symbols in help texts: say them as words (TTS engines skip them)
const SYM = {
  de: { q: 'Fragezeichen', then: ', dann', menu: 'Drei-Punkte-Menü', x: 'Kreuz', ok: 'Haken', plus: 'Plus', and: 'und', pm: 'plus minus', deg: 'Grad', mic: 'Mikrofon', eye: 'Auge', gear: 'Zahnrad', pin: 'Standort', spk: 'Lautsprecher', bulb: 'Tipp', warn: 'Achtung', basket: 'Korb', robot: 'Roboter', cam: 'Kamera', refresh: 'Neu laden' },
  sq: { q: 'pikëpyetje', then: ', pastaj', menu: 'menyja me tre pika', x: 'kryq', ok: 'shenjë', plus: 'plus', and: 'dhe', pm: 'plus minus', deg: 'gradë', mic: 'mikrofon', eye: 'sy', gear: 'ingranazh', pin: 'vendndodhja', spk: 'altoparlant', bulb: 'këshillë', warn: 'paralajmërim', basket: 'shportë', robot: 'robot', cam: 'kamera', refresh: 'rifresko' },
  en: { q: 'question mark', then: ', then', menu: 'three-dot menu', x: 'cross', ok: 'tick', plus: 'plus', and: 'and', pm: 'plus minus', deg: 'degrees', mic: 'microphone', eye: 'eye', gear: 'gear', pin: 'location', spk: 'speaker', bulb: 'tip', warn: 'warning', basket: 'basket', robot: 'robot', cam: 'camera', refresh: 'reload' },
  it: { q: 'punto interrogativo', then: ', poi', menu: 'menu a tre punti', x: 'croce', ok: 'spunta', plus: 'più', and: 'e', pm: 'più meno', deg: 'gradi', mic: 'microfono', eye: 'occhio', gear: 'ingranaggio', pin: 'posizione', spk: 'altoparlante', bulb: 'suggerimento', warn: 'attenzione', basket: 'cesto', robot: 'robot', cam: 'fotocamera', refresh: 'ricarica' },
  es: { q: 'signo de interrogación', then: ', luego', menu: 'menú de tres puntos', x: 'cruz', ok: 'marca', plus: 'más', and: 'y', pm: 'más menos', deg: 'grados', mic: 'micrófono', eye: 'ojo', gear: 'engranaje', pin: 'ubicación', spk: 'altavoz', bulb: 'consejo', warn: 'atención', basket: 'cesta', robot: 'robot', cam: 'cámara', refresh: 'recargar' },
  pt: { q: 'ponto de interrogação', then: ', depois', menu: 'menu de três pontos', x: 'cruz', ok: 'visto', plus: 'mais', and: 'e', pm: 'mais menos', deg: 'graus', mic: 'microfone', eye: 'olho', gear: 'engrenagem', pin: 'localização', spk: 'altifalante', bulb: 'dica', warn: 'atenção', basket: 'cesto', robot: 'robô', cam: 'câmara', refresh: 'recarregar' },
  fr: { q: 'point d’interrogation', then: ', puis', menu: 'menu à trois points', x: 'croix', ok: 'coche', plus: 'plus', and: 'et', pm: 'plus ou moins', deg: 'degrés', mic: 'micro', eye: 'œil', gear: 'roue dentée', pin: 'position', spk: 'haut-parleur', bulb: 'astuce', warn: 'attention', basket: 'panier', robot: 'robot', cam: 'caméra', refresh: 'recharger' },
};
const EMO = { mic: /🎤|🎙️?/gu, eye: /👁️?/gu, gear: /⚙️?/gu, pin: /📍/gu, spk: /🔊|🔈|🔉/gu, bulb: /💡/gu, warn: /⚠️?/gu, basket: /🧺/gu, robot: /🤖/gu, cam: /📷|📸/gu, refresh: /🔄/gu, x: /[✕✖×]/gu, ok: /[✓✔✅]/gu };
function speakSymbols(text, l) {
  const w = SYM[l] || SYM.en;
  let s = text;
  Object.entries(EMO).forEach(([k, re]) => { s = s.replace(re, ` ${w[k]} `); });
  return s
    .replace(/(^|[\s(„“"'«])[?？](?=[\s)“”"'».,;:!]|$)/gu, `$1 ${w.q} `)
    .replace(/\s*(›|»(?=\s)|→|->|=>|⇒)\s*/gu, `${w.then} `)
    .replace(/[⋮⋯]\s*(menu|menü|menú)?/giu, ` ${w.menu} `)
    .replace(/(^|\s)\+(?=\s)/gu, `$1${w.plus}`)
    .replace(/\s&\s/gu, ` ${w.and} `)
    .replace(/±/gu, ` ${w.pm} `)
    .replace(/(\d)\s?°/gu, `$1 ${w.deg}`)
    .replace(/\s[–—]\s/gu, ', ')
    .replace(/\s*…/gu, '.')
    .replace(/\p{Extended_Pictographic}\uFE0F?/gu, ' ');
}
const lang = () => {
  const s = localStorage.getItem('baustelle_app_lang'), n = (navigator.language || 'de').slice(0, 2).toLowerCase();
  return s || (LOC[n] ? n : 'de');
};
const FEM = /female|weiblich|frau|anna|petra|helena|katja|hedda|marlene|vicki|sabine|amelie|zira|hazel|susan|samantha|karen|moira|tessa|fiona|victoria|serena|kate|libby|sonia|elsa|alice|paulina|monica|laura|lucia|joana|luciana|amelia|audrey|aurelie|julie|denise|seraphina|katja|eva|sara|google deutsch|google français|google italiano|google español/i;
const MAL = /\bmale\b|männlich|markus|stefan|hans|yannick|conrad|killian|daniel|david|george|mark|ryan|thomas|alex|fred|oliver|jorge|diego|pablo|luca|cosimo|diego|duarte|antonio|henri|paul|guy|reed|rocko|grandpa/i;
export const voiceGender = (v) => (MAL.test(v.name) && !/female/i.test(v.name) ? 'm' : FEM.test(v.name) ? 'f' : '');
export const voicesFor = (l = lang()) => (window.speechSynthesis?.getVoices() || []).filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(l));
export const speechLang = lang;
export function pickVoice(l = lang()) {
  const a = getA11y(), list = voicesFor(l);
  return list.find((v) => v.name === a.voiceName) || list.find((v) => voiceGender(v) === (a.gender || 'f') && v.localService) || list.find((v) => voiceGender(v) === (a.gender || 'f')) || list.find((v) => v.default) || list[0] || null;
}
// Android Chrome garbles / switches voice when speak() follows cancel() at once, and cuts long texts:
// wait a moment after cancel, speak sentence by sentence, keep utterances referenced
let seq = 0, keep = [];
function deviceSpeak(txt, l) {
  const ss = window.speechSynthesis;
  if (!ss) return;
  const my = ++seq, busy = ss.speaking || ss.pending;
  ss.cancel();
  setTimeout(() => {
    if (my !== seq) return;
    const v = pickVoice(l);
    if (l === 'sq' && !v) return; // no Albanian voice on this device: text only
    const parts = txt.split(/(?<=[.!?])\s+(?=\p{Lu}|\d)/u);
    keep = parts.map((p) => p.trim()).filter(Boolean).map((p) => {
      const u = new SpeechSynthesisUtterance(p);
      u.lang = v?.lang || LOC[l] || 'de-DE';
      if (v) u.voice = v;
      u.rate = 1;
      return u;
    });
    keep.forEach((u) => ss.speak(u));
  }, busy ? 250 : 30);
}
export function speak(text, force = false) {
  if ((!force && !getA11y().voice) || !text) return;
  window.dispatchEvent(new Event('giss-screen-wake'));
  const l = lang();
  if (playStored(text, l)) return;
  deviceSpeak(expandAbbr(text, l), l);
}

// one-shot voice command: resolves to 'point' | 'undo' | 'finish' | 'cancel' | null
const WORDS = {
  point: ['punkt', 'point', 'punto', 'ponto', 'setzen', 'pikë', 'vendos'], undo: ['rückgängig', 'zurück', 'undo', 'annulla', 'deshacer', 'desfazer', 'annuler', 'zhbëj', 'kthe'],
  finish: ['fertig', 'finish', 'done', 'fine', 'terminar', 'concluir', 'terminer', 'mbaro', 'përfundo'], cancel: ['abbrechen', 'cancel', 'interrompi', 'cancelar', 'abandonner', 'anulo'],
};
export function listenCommand() {
  const R = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!R) return Promise.resolve(null);
  return new Promise((res) => {
    const r = new R();
    r.lang = LOC[lang()] || 'de-DE'; r.interimResults = false; r.maxAlternatives = 3;
    r.onresult = (e) => {
      const said = [...e.results[0]].map((a) => a.transcript.toLowerCase()).join(' ');
      res(Object.keys(WORDS).find((k) => WORDS[k].some((w) => said.includes(w))) || null);
    };
    r.onerror = () => res(null);
    r.onend = () => res(null);
    r.start();
  });
}
